import { supabase } from "@/platform/lib/supabase";
import type {
  PlatformSubscriptionRow,
  PlatformTenantSlug,
} from "@/platform/lib/database.types";
import type { PlanSlug } from "@/platform/billing/plans";

const FUNCTIONS_BASE = "/api/billing";

/**
 * Cria sessão Stripe Checkout via Netlify Function e devolve a URL para
 * redirect. O backend valida price IDs e injeta trial_period_days.
 */
export async function createCheckoutSession(
  tenant: PlatformTenantSlug,
  plan: PlanSlug,
): Promise<{ url: string }> {
  const { data: sessionData, error } = await supabase.auth.getSession();
  if (error) throw error;
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error("Sessão inválida — faça login antes.");

  const response = await fetch(`${FUNCTIONS_BASE}/checkout`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ tenant, plan }),
  });

  if (!response.ok) {
    const errBody = await response.json().catch(() => ({ error: "Falha no checkout" }));
    throw new Error((errBody as { error: string }).error);
  }

  return (await response.json()) as { url: string };
}

/**
 * Subscriptions ativas (trialing | active | past_due) da usuária autenticada.
 */
export async function listActiveSubscriptions(): Promise<PlatformSubscriptionRow[]> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return [];

  const { data, error } = await supabase
    .from("platform_subscriptions")
    .select()
    .eq("user_id", userData.user.id)
    .in("status", ["trialing", "active", "past_due"]);
  if (error) throw error;
  return (data ?? []) as PlatformSubscriptionRow[];
}

/**
 * Indica se a usuária tem assinatura ativa para um tenant específico.
 */
export async function hasActiveSubscription(tenant: PlatformTenantSlug): Promise<boolean> {
  const subs = await listActiveSubscriptions();
  return subs.some((s) => s.tenant === tenant);
}
